import React from 'react';
import { CssBaseline } from '@mui/material';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, HeadContent, Scripts } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CatchBoundary from '@/components/catch-boundary';
import NotFound from '@/components/not-found';
import { AuthenticationProvider } from '@/components/providers/authentication-provider';
import { ThemeProvider } from '@/components/providers/theme-provider';
import { TanStackQueryDevtools } from '@/lib/devtools';

interface MyRouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  ssr: false,
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0',
      },
      {
        title: 'Ack Paperwork - All My Sons',
      },
    ],
    links: [
      {
        rel: 'icon',
        type: 'image/svg+xml',
        href: '/ams-logo.svg',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.googleapis.com',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap',
      },
    ],
  }),
  errorComponent: (props) => {
    return (
      <RootShell>
        <CatchBoundary {...props} />
      </RootShell>
    );
  },
  notFoundComponent: () => <NotFound />,
  shellComponent: RootShell,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, [setMounted]);

  return (
    <html suppressHydrationWarning dir="ltr" lang="en">
      <head>
        <script> const global = globalThis; </script>
        <HeadContent />
      </head>
      <body>
        <ThemeProvider>
          <CssBaseline />
          <ToastContainer position="top-right" autoClose={5000} />
          {mounted && <AuthenticationProvider>{children}</AuthenticationProvider>}
        </ThemeProvider>
        <TanStackDevtools
          config={{
            position: 'bottom-right',
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
            TanStackQueryDevtools,
          ]}
        />
        <Scripts />
      </body>
    </html>
  );
}
